import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CalendarPlus, MagicWand, Plus } from "@phosphor-icons/react";
import api from "@/lib/api";
import SeasonalThemeCard from "@/pages/admin/SeasonalThemeCard";
import CountryPicker from "@/pages/admin/CountryPicker";
import ShowingNowPanel from "@/pages/admin/ShowingNowPanel";
import SuggestThemesButton from "@/pages/admin/SuggestThemesButton";
import CoveragePanel from "@/pages/admin/CoveragePanel";
import SeasonalCalendar from "@/pages/admin/SeasonalCalendar";
import { useClashGuard } from "@/pages/admin/ThemeClashDialog";

const EMPTY = { name: "", idea: "", starts_on: "", ends_on: "", countries: [] };

export default function SeasonalThemesPanel() {
  const [rows, setRows] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [adding, setAdding] = useState(false);
  const [showEnded, setShowEnded] = useState(false);
  const { guard, dialog } = useClashGuard();

  const load = () => api.get("/admin/seasonal-themes").then((r) => setRows(r.data)).catch(() => toast.error("Could not load themes"));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!rows?.some((t) => t.gen_status === "generating")) return undefined;
    const t = setTimeout(load, 5000);
    return () => clearTimeout(t);
  }, [rows]);

  const prefill = async () => {
    try { const { data } = await api.post("/admin/seasonal-themes/prefill"); toast.success(data.created ? `Added ${data.created} holidays for the year ahead` : "The year ahead is already set up"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not pre-fill"); }
  };
  const create = () => guard(form, null, async () => {
    try { await api.post("/admin/seasonal-themes", form); toast.success("Theme added — now generate its artwork"); setForm(EMPTY); setAdding(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not add theme"); }
  });

  const pending = (rows || []).filter((t) => t.window_status === "upcoming" && !t.hero_url && t.gen_status !== "generating");
  const inFlight = (rows || []).filter((t) => t.gen_status === "generating").length;
  const bulk = async () => {
    if (!pending.length) { toast.info("Every upcoming theme already has artwork"); return; }
    if (!window.confirm(`Make artwork for ${pending.length} upcoming theme${pending.length === 1 ? "" : "s"}? They stay as drafts until you approve each one.`)) return;
    try { const { data } = await api.post("/admin/seasonal-themes/generate-drafts"); toast.success(`Creating artwork for ${data.started} theme${data.started === 1 ? "" : "s"} — about 30 seconds per pair`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start"); }
  };

  const visible = (rows || []).filter((t) => showEnded || t.window_status !== "ended");
  return (
    <div data-testid="admin-seasonal-panel">
      <p className="overline mb-2">Site look</p>
      <h2 className="font-display text-3xl font-black text-rf-ink mb-1">Seasonal themes</h2>
      <p className="text-rf-ink-soft mb-6 text-sm max-w-3xl">
        Festive artwork for the homepage hero and a decorative strip on Directory, Pricing, FAQs, News, Kitchen Help and Historical — never on cookbooks or recipe pages.
        Generate, tweak with a prompt until you love it, approve, and it shows on its dates (UK time) for the countries you choose.
      </p>
      <div className="flex flex-wrap gap-2 mb-6">
        <button type="button" onClick={prefill} data-testid="seasonal-prefill" className="btn-amber !py-2 !px-4 text-sm inline-flex items-center gap-1"><CalendarPlus size={16} /> Pre-fill the year ahead</button>
        <SuggestThemesButton onAdded={load} />
        <button type="button" onClick={() => setAdding((v) => !v)} data-testid="seasonal-add" className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-1"><Plus size={16} /> Add a theme</button>
        <button type="button" onClick={bulk} disabled={!rows} data-testid="seasonal-generate-all" className="btn-outline-ink !py-2 !px-4 text-sm inline-flex items-center gap-1"><MagicWand size={16} /> Generate all drafts{pending.length ? ` (${pending.length})` : ""}</button>
        {inFlight > 0 && <span className="self-center text-xs font-bold text-rf-ink-soft" data-testid="seasonal-progress">Creating artwork… {inFlight} left</span>}
        <label className="flex items-center gap-2 text-xs font-bold text-rf-ink-soft ml-auto">
          <input type="checkbox" checked={showEnded} onChange={(e) => setShowEnded(e.target.checked)} data-testid="seasonal-show-ended" className="w-4 h-4" /> Show ended
        </label>
      </div>
      <ShowingNowPanel />
      <CoveragePanel rows={rows} />
      <SeasonalCalendar rows={rows} />
      {dialog}
      {adding && (
        <div className="card-recipe !p-5 mb-6 grid md:grid-cols-2 gap-3" data-testid="seasonal-add-form">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name, e.g. Burns Night 2027" className="input-rf" data-testid="seasonal-new-name" />
          <input value={form.idea} onChange={(e) => setForm({ ...form, idea: e.target.value })} placeholder="What should the art show? e.g. haggis, neeps, tartan" className="input-rf" data-testid="seasonal-new-idea" />
          <label className="text-xs font-bold">Starts (UK)<input type="date" value={form.starts_on} onChange={(e) => setForm({ ...form, starts_on: e.target.value })} className="input-rf mt-1" data-testid="seasonal-new-start" /></label>
          <label className="text-xs font-bold">Ends (UK)<input type="date" value={form.ends_on} min={form.starts_on} onChange={(e) => setForm({ ...form, ends_on: e.target.value })} className="input-rf mt-1" data-testid="seasonal-new-end" /></label>
          <div className="md:col-span-2"><CountryPicker value={form.countries} onChange={(c) => setForm({ ...form, countries: c })} testid="seasonal-new-countries" /></div>
          <button type="button" onClick={create} className="btn-amber !py-2 !px-4 text-sm justify-self-start" data-testid="seasonal-new-save">Add theme</button>
        </div>
      )}
      {!rows && <p className="text-rf-ink-soft">Loading…</p>}
      {rows && visible.length === 0 && <p className="text-rf-ink-soft" data-testid="seasonal-empty">No themes yet — pre-fill the year ahead or add one.</p>}
      <div className="space-y-4">{visible.map((t) => <SeasonalThemeCard key={t.theme_id} t={t} onChanged={load} />)}</div>
    </div>
  );
}
